import React, { ForwardedRef, forwardRef, useEffect, useMemo, useRef } from 'react';
import SimpleVector from './SimpleVector';
import { Group, MathUtils } from 'three';
import JokeSphere from './JokeSphere';
import useDefaultValue from '../hooks/useDefaultValue';
import { GroupProps } from '@react-three/fiber/dist/declarations/src/three-types';
import FocusPoints from '../../../data/FocusPoints';
import { useFocusContext } from '../context/FocusContext';
import { Vector3OrArray } from '../../../types';
import { useDetailViewContext } from '../../../context/DetailViewContext';

export type JokeComponentProps = GroupProps & {
    arrowOrigin?: Vector3OrArray,
    sphereCenter: Vector3OrArray,
    sphereTransition?: number,
    sphereSize: number,
    sphereOutline?: number,
    color: number,
    hideShading?: boolean,
    label?: string,
    children?: React.ReactNode,
    focusID?: FocusPoints
};

const JokeComponent = forwardRef(({
  arrowOrigin,
  sphereTransition,
  sphereCenter,
  sphereSize,
  sphereOutline,
  hideShading,
  children,
  focusID,
  color,
  ...props
}: JokeComponentProps, ref?: ForwardedRef<Group>) =>
{
  const _sphereTransition = useDefaultValue(sphereTransition, 1);

  const dotSize = MathUtils.lerp(0.008, 0.004, _sphereTransition);
  const _sphereSize = useMemo(() => sphereSize * _sphereTransition, [ sphereSize, _sphereTransition ]);

  const { setActiveFocusID } = useDetailViewContext();
  const { setFocusPoint } = useFocusContext();
  const mainRef = useRef<Group>(null);

  const _onClick = useMemo(() =>
  {

    if (focusID === undefined) return undefined;
    return () =>
    {
      setActiveFocusID(focusID);
    };
  }, [ focusID, setActiveFocusID ]);

  useEffect(() =>
  {
    if (focusID !== undefined && mainRef.current) {
      setFocusPoint(focusID, mainRef.current, sphereSize);
    }
  }, [ focusID, setFocusPoint, sphereSize ]);

  return (<group visible={sphereSize > 0.0} ref={ref}>
    {arrowOrigin && <SimpleVector position={arrowOrigin} color={color} target={sphereCenter} dotSize={dotSize}/>}
    <JokeSphere {...props} color={color} position={sphereCenter} size={_sphereSize} ref={mainRef}
      outline={sphereOutline} hideShading={hideShading} onClick={_onClick}>
      {children}
    </JokeSphere>
  </group>);
});

JokeComponent.displayName = 'JokeComponent';

export default JokeComponent;